import { z } from 'zod/v4';

import { MIN_CHECKOUT_SOURCE_QUANTITY, MAX_CHECKOUT_SOURCE_QUANTITY } from '@/features/organizations/billing-constants';
import { ApiClientError, api } from '@/lib/api';
import {
    MOCK_BILLING_PRODUCTS,
    createMockBillingState,
    createMockCheckoutSession,
    createMockCustomerPortalSession
} from './billing-mocks';

export const billingIntervalSchema = z.enum(['monthly']);

export const billingSourceSchema = z.enum(['stripe', 'lifetime']);

function isAllowedRedirectUrl(value: string): boolean {
    try {
        const url = new URL(value);

        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return false;
        }

        if (typeof window !== 'undefined') {
            return url.origin === window.location.origin;
        }

        return url.origin === 'http://localhost' || url.origin === 'https://localhost';
    } catch {
        return false;
    }
}

const redirectUrlSchema = z.string().refine(isAllowedRedirectUrl, {
    message: 'Invalid redirect URL'
});

const entitlementsMapSchema = z.union([z.record(z.string(), z.unknown()), z.array(z.unknown())]).transform((value) => {
    if (Array.isArray(value)) {
        return {} as Record<string, unknown>;
    }

    return value;
});

export const billingProductSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    description: z.string().nullable().optional(),
    isActive: z.boolean(),
    capabilities: entitlementsMapSchema,
    limits: entitlementsMapSchema
});

export const billingProductDetailSchema = billingProductSchema.extend({
    stripeMonthlyPriceId: z.string().nullable().optional(),
    stripeYearlyPriceId: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string()
});

export const createStripeCheckoutSessionInputSchema = z.object({
    successUrl: redirectUrlSchema,
    cancelUrl: redirectUrlSchema,
    interval: billingIntervalSchema.default('monthly'),
    quantity: z.int().min(MIN_CHECKOUT_SOURCE_QUANTITY).max(MAX_CHECKOUT_SOURCE_QUANTITY)
});

export const createStripeCustomerPortalSessionInputSchema = z.object({
    returnUrl: redirectUrlSchema
});

export const stripeSessionResponseSchema = z.object({
    sessionId: z.string(),
    url: z.url()
});

export const organizationBillingSchema = z.object({
    organizationId: z.string(),
    product: billingProductDetailSchema.nullable().optional(),
    subscriptionId: z.string().nullable().optional(),
    subscriptionStatus: z.string().nullable().optional(),
    source: billingSourceSchema.nullable().optional().catch(null),
    billingInterval: z.string().nullable().optional(),
    stripeCustomerId: z.string().nullable().optional(),
    stripeSubscriptionId: z.string().nullable().optional(),
    stripePriceId: z.string().nullable().optional(),
    currentPeriodStart: z.string().nullable().optional(),
    currentPeriodEnd: z.string().nullable().optional(),
    cancelAtPeriodEnd: z.boolean().nullable().optional(),
    canceledAt: z.string().nullable().optional(),
    trialEndsAt: z.string().nullable().optional(),
    quantity: z.number().int().min(1).nullable().optional(),
    hasActivePlatformAccess: z.boolean().optional(),
    capabilities: entitlementsMapSchema,
    limits: entitlementsMapSchema
});

export type BillingInterval = z.infer<typeof billingIntervalSchema>;
export type BillingSource = z.infer<typeof billingSourceSchema>;
export type BillingProduct = z.infer<typeof billingProductSchema>;
export type BillingProductDetail = z.infer<typeof billingProductDetailSchema>;
export type CreateStripeCheckoutSessionInput = z.infer<typeof createStripeCheckoutSessionInputSchema>;
export type CreateStripeCustomerPortalSessionInput = z.infer<typeof createStripeCustomerPortalSessionInputSchema>;
export type StripeSessionResponse = z.infer<typeof stripeSessionResponseSchema>;
export type OrganizationBilling = z.infer<typeof organizationBillingSchema>;

function shouldUseBillingMocks(error: unknown): boolean {
    return error instanceof ApiClientError && [404, 405, 501, 502, 503].includes(error.status);
}

export async function listBillingProducts(): Promise<BillingProduct[]> {
    try {
        const response = await api.get('/v1/billing/products', { authenticated: true });
        return z.array(billingProductSchema).parse(response);
    } catch (error) {
        if (shouldUseBillingMocks(error)) {
            return MOCK_BILLING_PRODUCTS;
        }

        throw error;
    }
}

export async function getOrganizationBilling(organizationId: string): Promise<OrganizationBilling> {
    try {
        const response = await api.get(`/v1/organizations/${organizationId}/billing`, { authenticated: true });
        return organizationBillingSchema.parse(response);
    } catch (error) {
        if (shouldUseBillingMocks(error)) {
            return createMockBillingState(organizationId);
        }

        throw error;
    }
}

export async function createCheckoutSession(args: {
    organizationId: string;
    productId: string;
    input: CreateStripeCheckoutSessionInput;
    allowMockFallback?: boolean;
}): Promise<StripeSessionResponse> {
    const input = createStripeCheckoutSessionInputSchema.parse(args.input);

    try {
        const response = await api.post(
            `/v1/organizations/${args.organizationId}/billing/checkout-session/${args.productId}`,
            input,
            { authenticated: true }
        );

        return stripeSessionResponseSchema.parse(response);
    } catch (error) {
        if (args.allowMockFallback !== false && shouldUseBillingMocks(error)) {
            return createMockCheckoutSession(input.successUrl);
        }

        throw error;
    }
}

export async function createCustomerPortalSession(args: {
    organizationId: string;
    input: CreateStripeCustomerPortalSessionInput;
}): Promise<StripeSessionResponse> {
    const input = createStripeCustomerPortalSessionInputSchema.parse(args.input);

    try {
        const response = await api.post(
            `/v1/organizations/${args.organizationId}/billing/customer-portal-session`,
            input,
            {
                authenticated: true
            }
        );

        return stripeSessionResponseSchema.parse(response);
    } catch (error) {
        if (shouldUseBillingMocks(error)) {
            return createMockCustomerPortalSession(input.returnUrl);
        }

        throw error;
    }
}
